import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Loader2, Plus, Trash2, Pencil, Save, X, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../../lib/api";

const EMPTY = {
  title: "",
  division: "all",
  location: "",
  date_text: "",
  deadline: "",
  brief: "",
  requirements: "",
  open: true,
  order: 0,
};

export default function CastingsManager() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {...EMPTY} | {...doc}

  const load = () => {
    setLoading(true);
    api.get("/admin/castings")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    load();
  }, [navigate]);

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try {
      await api.delete(`/admin/castings/${it.id}`);
      toast.success("Casting removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleOpen = async (it) => {
    try {
      const { id, created_at, ...payload } = it;
      await api.put(`/admin/castings/${it.id}`, { ...payload, open: !it.open });
      toast.success(it.open ? "Casting closed" : "Casting reopened");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-[#0A0A0A] pt-32 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="castings-manager-page" className="min-h-screen bg-white text-[#0A0A0A] pt-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
        <Link
          to="/admin"
          data-testid="castings-manager-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black"
        >
          <ChevronLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="label-overline text-zinc-500">Site content · 04</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light mt-3 leading-none">
              Now Casting.
            </h1>
            <p className="text-zinc-600 mt-3 text-sm max-w-2xl leading-relaxed">
              Open calls published on the public <Link to="/casting" className="underline hover:text-black">/casting</Link> board.
              Only castings marked <span className="font-medium text-zinc-700">open</span> are visible to visitors.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="casting-add"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14}/> New casting
          </button>
        </div>

        {editing && (
          <EditorSheet
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        <div className="mt-12">
          {items.length === 0 && !editing && (
            <div className="border border-dashed border-black/15 bg-[#FAFAF9] p-12 text-center">
              <Megaphone size={20} className="mx-auto text-zinc-300"/>
              <p className="label-overline text-zinc-400 mt-3">No castings yet</p>
              <p className="text-sm text-zinc-500 mt-2">
                Click <span className="font-medium text-zinc-700">New casting</span> to publish your first open call.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`casting-admin-row-${it.id}`}
                className="flex items-stretch gap-4 border border-black/10 bg-white p-4"
              >
                <div className="flex-1 min-w-0 py-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif text-lg leading-tight truncate">{it.title}</p>
                    <span
                      className={`label-overline text-[10px] px-2 py-0.5 ${
                        it.open ? "bg-black text-white" : "bg-zinc-200 text-zinc-500"
                      }`}
                    >
                      {it.open ? "Open" : "Closed"}
                    </span>
                  </div>
                  <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1.5">
                    {[
                      it.division === "all" ? "Women & Men" : it.division,
                      it.location,
                      it.date_text,
                    ].filter(Boolean).join(" · ")}
                  </p>
                  {it.brief && (
                    <p className="text-sm text-zinc-600 mt-2 line-clamp-1">{it.brief}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 self-center">
                  <button
                    type="button"
                    onClick={() => toggleOpen(it)}
                    data-testid={`casting-toggle-${it.id}`}
                    className="px-3 py-2 text-xs label-overline text-zinc-600 hover:text-black hover:bg-black/5 transition-colors"
                  >
                    {it.open ? "Close" : "Reopen"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(it)}
                    data-testid={`casting-edit-${it.id}`}
                    className="p-2 text-zinc-600 hover:text-black hover:bg-black/5 transition-colors"
                    aria-label={`Edit ${it.title}`}
                  >
                    <Pencil size={14}/>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it)}
                    data-testid={`casting-delete-${it.id}`}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                    aria-label={`Delete ${it.title}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditorSheet({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    title: initial.title || "",
    division: initial.division || "all",
    location: initial.location || "",
    date_text: initial.date_text || "",
    deadline: initial.deadline || "",
    brief: initial.brief || "",
    requirements: initial.requirements || "",
    open: initial.open ?? true,
    order: initial.order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        division: form.division,
        location: form.location.trim() || null,
        date_text: form.date_text.trim() || null,
        deadline: form.deadline.trim() || null,
        brief: form.brief.trim() || null,
        requirements: form.requirements.trim() || null,
        open: !!form.open,
        order: Number(form.order) || 0,
      };
      if (isNew) {
        await api.post("/admin/castings", payload);
        toast.success("Casting published");
      } else {
        await api.put(`/admin/castings/${initial.id}`, payload);
        toast.success("Casting updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="casting-editor" className="mt-8 border border-black/10 bg-[#FAFAF9] p-6 space-y-5">
      <div className="flex items-center justify-between">
        <p className="label-overline text-zinc-500">{isNew ? "New casting" : `Editing · ${initial.title}`}</p>
        <button type="button" onClick={onCancel} className="p-1 text-zinc-500 hover:text-black" aria-label="Cancel">
          <X size={16}/>
        </button>
      </div>

      <TextField
        label="Title *"
        value={form.title}
        onChange={(v) => set("title", v)}
        placeholder="e.g. SS27 campaign — European faces"
        testid="casting-title"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-overline text-zinc-700">Division</label>
          <select
            value={form.division}
            onChange={(e) => set("division", e.target.value)}
            data-testid="casting-division"
            className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black"
          >
            <option value="all">Women &amp; Men</option>
            <option value="women">Women</option>
            <option value="men">Men</option>
          </select>
        </div>
        <TextField
          label="Location"
          value={form.location}
          onChange={(v) => set("location", v)}
          placeholder="e.g. Amsterdam"
          testid="casting-location"
        />
        <TextField
          label="Dates"
          value={form.date_text}
          onChange={(v) => set("date_text", v)}
          placeholder="e.g. 12–14 August 2026"
          testid="casting-dates"
        />
      </div>

      <div>
        <label className="label-overline text-zinc-700">Brief</label>
        <textarea
          rows={3}
          value={form.brief}
          onChange={(e) => set("brief", e.target.value)}
          placeholder="What is this casting for? A short paragraph shown on the public board."
          data-testid="casting-brief"
          className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black resize-y"
        />
      </div>

      <div>
        <label className="label-overline text-zinc-700">Looking for</label>
        <textarea
          rows={2}
          value={form.requirements}
          onChange={(e) => set("requirements", e.target.value)}
          placeholder="e.g. 170 cm+, ages 16–24, no experience required"
          data-testid="casting-requirements"
          className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black resize-y"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <TextField
          label="Deadline"
          value={form.deadline}
          onChange={(v) => set("deadline", v)}
          placeholder="e.g. Apply before 1 August"
          testid="casting-deadline"
        />
        <TextField
          label="Order"
          value={String(form.order)}
          onChange={(v) => set("order", v)}
          placeholder="0 = first"
          type="number"
          testid="casting-order"
        />
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.open}
              onChange={(e) => set("open", e.target.checked)}
              data-testid="casting-open"
              className="accent-black w-4 h-4"
            />
            <span className="label-overline text-zinc-700">Open (visible on site)</span>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-3 border-t border-black/10">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="casting-save"
          className="btn-primary inline-flex items-center gap-2"
        >
          {saving ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Save size={14}/> {isNew ? "Publish" : "Save changes"}</>}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="label-overline text-zinc-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black"
      />
    </div>
  );
}
